<template>
  <div>
    <a-alert
      type="info"
      show-icon
      :message="$t('appreciation.automatic')"
      :description="$t('appreciation.automaticDescription')"
      style="margin-bottom: 16px"
    />

    <a-card size="small" style="margin-bottom: 16px">
      <a-form>
        <a-row :gutter="16">
          <a-col :span="8">
            <a-form-item :label="$t('scolarite.moduleType')">
              <a-select
                v-model="filters.moduleType"
                style="width: 100%"
                :placeholder="$t('all.select')"
              >
                <a-select-option value="MP">
                  {{ $t("emploi.modulesPedagogiques") }}
                </a-select-option>
                <a-select-option value="MT">
                  {{ $t("emploi.modulesPilotes") }}
                </a-select-option>
                <a-select-option value="MS">
                  {{ $t("scolarite.modulesVerySpec") }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>

          <a-col :span="8">
            <a-form-item :label="$t('etablissement.niveau')">
              <a-select v-model="filters.level" style="width: 100%">
                <a-select-option
                  v-for="(value, name, index) in levelSelectData"
                  :key="index"
                  :value="name"
                >
                  {{ value }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>

          <a-col :span="8">
            <a-form-item :label="$t('college.bulletin.config.trimestres')">
              <a-select
                v-model="filters.trimester"
                style="width: 100%"
                :placeholder="$t('college.bulletin.config.trimestres')"
              >
                <a-select-option value="1">1</a-select-option>
                <a-select-option value="2">2</a-select-option>
                <a-select-option value="3">3</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="availableSubmodules.length > 0 ? 12 : 24">
            <a-form-item :label="$t('scolarite.nomModule')">
              <a-select
                v-model="filters.moduleId"
                show-search
                option-filter-prop="children"
                style="width: 100%"
                :placeholder="$t('scolarite.nomModule')"
                :filter-option="filterOption"
                @change="handleModuleChange"
              >
                <a-select-option
                  v-for="m in modules"
                  :key="m._id"
                  :value="m._id"
                >
                  {{ m.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>

          <a-col v-if="availableSubmodules.length > 0" :span="12">
            <a-form-item :label="$t('bulletin.subModule')">
              <a-select
                v-model="filters.submoduleId"
                show-search
                option-filter-prop="children"
                style="width: 100%"
                :placeholder="$t('emploi.chooseSubmodule')"
                :filter-option="filterOption"
                :allow-clear="canAppModule"
                @change="handleSubmoduleChange"
              >
                <a-select-option v-if="canAppModule" :value="null">
                  {{ $t("connected.all") }}
                </a-select-option>
                <a-select-option
                  v-for="sm in availableSubmodules"
                  :key="sm._id"
                  :value="sm._id"
                >
                  {{ sm.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <div
      class="mb-2"
      style="
        display: flex;
        gap: 8px;
        align-items: center;
        justify-content: space-between;
      "
    >
      <a-button
        icon="plus"
        type="primary"
        @click="showAddModal"
        :disabled="!hasValidFilters || isReadOnly"
      >
        {{ $t("all.add") }}
      </a-button>
      <div style="display: flex; gap: 8px; align-items: center">
        <a-button
          type="primary"
          icon="check"
          @click="applyAutomaticAppreciation"
          :disabled="!hasValidFilters || appreciations.length === 0 || isReadOnly"
        >
          {{ $t("appreciation.applyAutomatic") }}
        </a-button>
        <a-button type="primary" class="mx-auto my-2" @click="handleClone" :disabled="isReadOnly">
          {{ $t("action.clone") }}
        </a-button>
      </div>
    </div>

    <div class="card">
      <a-table
        :rowKey="'_id'"
        :columns="columns"
        :data-source="appreciations"
        :pagination="false"
        :loading="loading"
      >
        <template slot="min" slot-scope="text, record">
          {{ record.min }}
        </template>

        <template slot="max" slot-scope="text, record">
          {{ record.max }}
        </template>

        <template slot="appreciation" slot-scope="text, record">
          {{ record.appreciation }}
        </template>

        <template slot="submodule" slot-scope="text, record">
          <span v-if="record.submoduleId">
            {{
              record.submoduleId && record.submoduleId.name
                ? record.submoduleId.name
                : (
                    allSubmodules.find((sm) => sm._id === record.submoduleId) ||
                    {}
                  ).name || ""
            }}
          </span>
          <span v-else>{{ $t("all.none") }}</span>
        </template>

        <template slot="operation" slot-scope="text, record">
          <div style="display: flex; gap: 8px; align-items: center">
            <a-button type="primary" icon="edit" @click="showEditModal(record)" :disabled="isReadOnly">
              {{ $t("action.modifier") }}
            </a-button>
            <a-button
              type="danger"
              icon="delete"
              @click="removeAppreciation(record._id)"
              :disabled="isReadOnly"
            >
              {{ $t("action.supprimer") }}
            </a-button>
          </div>
        </template>
      </a-table>
    </div>

    <!-- Add/Edit Modal -->
    <a-modal
      v-model="visibleModal"
      :title="editingAppreciation ? $t('action.modifier') : $t('all.add')"
      @cancel="handleCancel"
      :footer="false"
    >
      <a-form :form="form" @submit="handleSubmit">
        <a-form-item :label="$t('certification.min')">
          <a-input-number
            v-decorator="[
              'min',
              {
                rules: [{ required: true, message: minRequiredMessage }],
                initialValue: editingAppreciation ? editingAppreciation.min : 0,
              },
            ]"
            :min="0"
            :max="20"
            style="width: 100%"
          />
        </a-form-item>

        <a-form-item :label="$t('certification.max')">
          <a-input-number
            v-decorator="[
              'max',
              {
                rules: [{ required: true, message: maxRequiredMessage }],
                initialValue: editingAppreciation
                  ? editingAppreciation.max
                  : 20,
              },
            ]"
            :min="0"
            :max="20"
            style="width: 100%"
          />
        </a-form-item>

        <a-form-item :label="$t('appreciation.appreciation')">
          <a-select
            v-decorator="[
              'appreciationPredefined',
              {
                initialValue:
                  editingAppreciation &&
                  predefinedAppreciations.find(
                    (a) => a.appreciation === editingAppreciation.appreciation
                  )
                    ? editingAppreciation.appreciation
                    : null,
              },
            ]"
            show-search
            option-filter-prop="children"
            :placeholder="$t('appreciation.selectPredefinedAppreciation')"
            :filter-option="filterOption"
            allow-clear
            @change="handlePredefinedChange"
          >
            <a-select-option
              v-for="option in predefinedAppreciations"
              :key="option.id"
              :value="option.appreciation"
            >
              {{ option.appreciation }}
            </a-select-option>
          </a-select>

          <a-divider orientation="center">{{ $t("all.or") }}</a-divider>

          <a-input
            v-decorator="[
              'appreciationCustom',
              {
                initialValue:
                  editingAppreciation &&
                  !predefinedAppreciations.find(
                    (a) => a.appreciation === editingAppreciation.appreciation
                  )
                    ? editingAppreciation.appreciation
                    : '',
              },
            ]"
            :placeholder="$t('appreciation.customAppreciation')"
            @change="handleCustomChange"
          />
        </a-form-item>

        <div class="form-actions mt-0">
          <a-button
            type="primary"
            htmlType="submit"
            :loading="saving"
            :disabled="isReadOnly"
            class="mr-3"
          >
            {{ $t("action.enregistrer") }}
          </a-button>
          <a-button key="back" @click="handleCancel">
            {{ $t("action.annuler") }}
          </a-button>
        </div>
      </a-form>
    </a-modal>

    <!-- Clone Modal -->
    <a-modal
      :visible="visibleCloneModal"
      centered
      @cancel="handleCloneCancel"
      width="70%"
      :footer="false"
    >
      <template slot="title">
        <div class="text-center">
          <h4 class="mb-0">
            {{ $t("appreciation.duplicateAppreciations") }}
          </h4>
        </div>
      </template>

      <div class="p-3">
        <div class="mb-4">
          <a-divider orientation="left">{{ $t("all.source") }}</a-divider>
          <div class="row g-3">
            <div class="col-md-3">
              <label class="form-label">{{ $t("scolarite.moduleType") }}</label>
              <a-select
                class="w-100"
                :value="cloneSource.moduleType"
                @change="cloneSource.moduleType = $event"
              >
                <a-select-option value="MP">
                  {{ $t("emploi.modulesPedagogiques") }}
                </a-select-option>
                <a-select-option value="MT">
                  {{ $t("emploi.modulesPilotes") }}
                </a-select-option>
                <a-select-option value="MS">
                  {{ $t("scolarite.modulesVerySpec") }}
                </a-select-option>
              </a-select>
            </div>

            <div class="col-md-2">
              <label class="form-label">{{ $t("etablissement.niveau") }}</label>
              <a-select
                class="w-100"
                :value="cloneSource.level"
                @change="cloneSource.level = $event"
              >
                <a-select-option
                  v-for="(value, name, index) in levelSelectData"
                  :key="index"
                  :value="name"
                >
                  {{ value }}
                </a-select-option>
              </a-select>
            </div>

            <div class="col-md-2">
              <label class="form-label">{{ $t("emploi.trimestre") }}</label>
              <a-select
                class="w-100"
                :value="cloneSource.trimester"
                @change="cloneSource.trimester = $event"
              >
                <a-select-option value="1">1</a-select-option>
                <a-select-option value="2">2</a-select-option>
                <a-select-option value="3">3</a-select-option>
              </a-select>
            </div>

            <div class="col-md-3">
              <label class="form-label">{{ $t("scolarite.moduleName") }}</label>
              <a-select
                class="w-100"
                :value="cloneSource.moduleId"
                @change="handleCloneSourceModuleChange"
                :placeholder="$t('scolarite.nomModule')"
                show-search
                :filter-option="filterOption"
              >
                <a-select-option
                  v-for="m in sourceModules"
                  :key="m._id"
                  :value="m._id"
                >
                  {{ m.name }}
                </a-select-option>
              </a-select>
            </div>

            <div
              v-if="cloneSourceAvailableSubmodules.length > 0"
              class="col-md-2"
            >
              <label class="form-label">{{ $t("bulletin.subModule") }}</label>
              <a-select
                class="w-100"
                :value="cloneSource.submoduleId"
                @change="cloneSource.submoduleId = $event"
                :placeholder="$t('emploi.chooseSubmodule')"
                show-search
                :filter-option="filterOption"
                :allow-clear="cloneSourceCanAppModule"
              >
                <a-select-option v-if="cloneSourceCanAppModule" :value="null">
                  {{ $t("connected.all") }}
                </a-select-option>
                <a-select-option
                  v-for="sm in cloneSourceAvailableSubmodules"
                  :key="sm._id"
                  :value="sm._id"
                >
                  {{ sm.name }}
                </a-select-option>
              </a-select>
            </div>

            <div class="col-md-2">
              <label class="form-label">{{ $t("scolarite.schoolYear") }}</label>
              <a-select
                class="w-100"
                :value="cloneSource.schoolYear"
                @change="cloneSource.schoolYear = $event"
                :placeholder="$t('scolarite.selectSchoolYear')"
              >
                <a-select-option
                  v-for="schoolYear in schoolYears"
                  :key="schoolYear._id"
                  :value="schoolYear._id"
                >
                  {{ schoolYear.label }}
                </a-select-option>
              </a-select>
            </div>
          </div>
        </div>

        <div class="mb-4">
          <a-divider orientation="left">{{ $t("all.targets") }}</a-divider>
          <div class="row g-3">
            <div class="col-md-2">
              <a-button
                type="primary"
                class="w-100 mt-4"
                @click="selectAllClone"
              >
                {{ $t("sms.selectAll") }}
              </a-button>
            </div>

            <div class="col-md-3">
              <label class="form-label">{{ $t("scolarite.moduleType") }}</label>
              <a-select
                class="w-100"
                :value="cloneTarget.moduleTypes"
                mode="multiple"
                allow-clear
                @change="cloneTarget.moduleTypes = $event"
                :placeholder="$t('all.selectTypes')"
              >
                <a-select-option value="MP">
                  {{ $t("emploi.modulesPedagogiques") }}
                </a-select-option>
                <a-select-option value="MT">
                  {{ $t("emploi.modulesPilotes") }}
                </a-select-option>
                <a-select-option value="MS">
                  {{ $t("scolarite.modulesVerySpec") }}
                </a-select-option>
              </a-select>
            </div>

            <div class="col-md-2">
              <label class="form-label">{{ $t("etablissement.niveau") }}</label>
              <a-select
                class="w-100"
                :value="cloneTarget.levels"
                mode="multiple"
                allow-clear
                @change="cloneTarget.levels = $event"
                :placeholder="$t('all.selectLevels')"
              >
                <a-select-option
                  v-for="(value, name, index) in levelSelectData"
                  :key="index"
                  :value="name"
                >
                  {{ value }}
                </a-select-option>
              </a-select>
            </div>

            <div class="col-md-2">
              <label class="form-label">{{ $t("emploi.trimestre") }}</label>
              <a-select
                class="w-100"
                mode="multiple"
                allow-clear
                :value="cloneTarget.trimesters"
                @change="cloneTarget.trimesters = $event"
                :placeholder="$t('college.bulletin.config.trimestres')"
              >
                <a-select-option value="1">1</a-select-option>
                <a-select-option value="2">2</a-select-option>
                <a-select-option value="3">3</a-select-option>
              </a-select>
            </div>

            <div class="col-md-3">
              <label class="form-label">{{ $t("scolarite.schoolYear") }}</label>
              <a-select
                class="w-100"
                :value="cloneTarget.schoolYear"
                @change="cloneTarget.schoolYear = $event"
                :placeholder="$t('college.bulletin.config.anneeScolaire')"
              >
                <a-select-option
                  v-for="schoolYear in schoolYears"
                  :key="schoolYear._id"
                  :value="schoolYear._id"
                >
                  {{ schoolYear.label }}
                </a-select-option>
              </a-select>
            </div>
          </div>
          <div class="row g-3 mt-2">
            <div class="col-md-12">
              <label class="form-label">{{ $t("scolarite.moduleName") }}</label>
              <a-select
                class="w-100"
                :value="cloneTarget.moduleIds"
                mode="multiple"
                allow-clear
                @change="cloneTarget.moduleIds = $event"
                :placeholder="$t('scolarite.nomModule')"
                show-search
                :filter-option="filterOption"
                :loading="loadingTargetModules"
              >
                <a-select-opt-group
                  v-for="(modules, level) in targetModulesByLevel"
                  :key="level"
                  :label="levelSelectData[level] || level"
                >
                  <a-select-option
                    v-for="m in modules"
                    :key="m._id"
                    :value="m._id"
                  >
                    {{ m.name }}
                  </a-select-option>
                </a-select-opt-group>
              </a-select>
            </div>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-md-4 offset-md-8">
            <a-button type="primary" class="w-100" @click="executeClone" :disabled="isReadOnly">
              <a-icon type="save" />
              {{ $t("all.apply") }}
            </a-button>
          </div>
        </div>
      </div>
    </a-modal>

    <!-- Existing Appreciations Modal -->
    <a-modal
      :visible="visibleExistingAppreciationsModal"
      :title="$t('appreciation.existingAppreciations')"
      @cancel="handleCancelExistingModal"
      width="70%"
      :footer="false"
    >
      <div v-if="loadingExistingAppreciations" class="text-center p-4">
        <a-spin />
      </div>
      <div
        v-else-if="existingAppreciations.length === 0"
        class="text-center p-4"
      >
        <p>
          {{ $t("appreciation.noExistingAppreciations") }}
        </p>
        <a-button
          type="primary"
          class="mt-3"
          @click="confirmApplyAppreciations"
          :disabled="isReadOnly"
        >
          {{ $t("appreciation.applyAutomatic") }}
        </a-button>
      </div>
      <div v-else>
        <p class="mb-3">
          {{ $t("appreciation.selectToReplace") }}
        </p>
        <a-table
          :rowKey="'_id'"
          :columns="existingAppreciationsColumns"
          :data-source="existingAppreciations"
          :pagination="{ pageSize: 10 }"
          :row-selection="{
            selectedRowKeys: selectedAppreciationIds,
            onChange: onSelectAppreciations,
          }"
        >
          <template slot="student" slot-scope="text, record">
            <span v-if="record.student">
              {{ record.student.firstName }} {{ record.student.lastName }}
            </span>
          </template>
          <template slot="classroom" slot-scope="text, record">
            <span v-if="record.classroom">{{ record.classroom.name }}</span>
          </template>
          <template slot="teacher" slot-scope="text, record">
            <span v-if="record.teacher && record.teacher.employee">
              {{ record.teacher.employee.firstName }}
              {{ record.teacher.employee.lastName }}
            </span>
            <span v-else>
              {{ $t("appreciation.noTeacherAssigned") }}
            </span>
          </template>
        </a-table>
      </div>
      <div class="mt-4 text-right" v-if="existingAppreciations.length > 0">
        <a-button @click="handleCancelExistingModal" class="mr-2">
          {{ $t("action.annuler") }}
        </a-button>
        <a-button
          type="primary"
          @click="confirmApplyAppreciations"
          :loading="applyingAppreciations"
          :disabled="isReadOnly"
        >
          {{ $t("all.apply") }}
        </a-button>
      </div>
    </a-modal>
  </div>
</template>

<script>
/* eslint-disable */
import apiClient from "@/services/axios";
import { mapState, mapGetters } from "vuex";
import { getLevels } from "@/config/LevelConstants";
import moment from "moment";

export default {
  name: "AutomaticAppreciation",
  beforeMount() {
    this.form = this.$form.createForm(this, { name: "AppreciationForm" });
  },
  data() {
    return {
      selectingAllClone: false,
      loading: false,
      saving: false,
      loadingModules: false,
      visibleModal: false,
      visibleCloneModal: false,
      editingAppreciation: null,
      form: this.$form.createForm(this),
      filters: {
        moduleType: null,
        level: null,
        trimester: null,
        moduleId: null,
        submoduleId: null,
      },
      availableSubmodules: [],
      allSubmodules: [],
      appreciations: [],
      predefinedAppreciations: [],
      schoolType: null,
      modules: [],
      cloneSource: {
        moduleType: null,
        level: null,
        trimester: null,
        moduleId: null,
        submoduleId: null,
        schoolYear: null,
      },
      cloneTarget: {
        moduleTypes: [],
        levels: [],
        trimesters: [],
        schoolYear: null,
        moduleIds: [],
      },
      sourceModules: [],
      targetModules: [],
      loadingTargetModules: false,
      loadingSourceModules: false,
      schoolYearsList: [],
      targetModulesTimeout: null,
      cloneSourceAvailableSubmodules: [],
      cloneSourceCanAppModule: false,
      canAppModule: false,
      visibleExistingAppreciationsModal: false,
      existingAppreciations: [],
      selectedAppreciationIds: [],
      applyingAppreciations: false,
      loadingExistingAppreciations: false,
    };
  },
  computed: {
    ...mapState(["settings", "user"]),
    ...mapGetters("settings", ["packDetailsGetter"]),
    activeBuilding() {
      return this.user.building.find(
        (el) => el.dbName == this.settings.activeBuilding
      );
    },
    countryCode() {
      return this.activeBuilding?.countryCode;
    },
    levelSelectData() {
      const levels = getLevels(this.$t.bind(this), this.schoolType, this.countryCode);
      return Object.keys(levels || {})
        .filter((key) => Number(key) <= 6)
        .reduce((acc, key) => {
          acc[key] = levels[key];
          return acc;
        }, {});
    },
    defaultPrimaryLevel() {
      const availableLevels = Object.keys(this.levelSelectData || {});
      return availableLevels.length > 0 ? availableLevels[0] : null;
    },
    isReadOnly() {
      const value = this.packDetailsGetter?.access?.edManager?.autoAppreciation;
      return value === "ro";
    },
    hasValidFilters() {
      return (
        this.filters.moduleType &&
        this.filters.level &&
        this.filters.trimester &&
        this.filters.moduleId
      );
    },
    minRequiredMessage() {
      return this.$t("requis.min");
    },
    maxRequiredMessage() {
      return this.$t("requis.max");
    },
    schoolYears() {
      return this.schoolYearsList.map((sy) => ({
        _id: sy._id,
        label: this.formatSchoolYear(sy),
      }));
    },
    columns() {
      return [
        {
          title: this.$t("certification.min"),
          dataIndex: "min",
          key: "min",
          scopedSlots: { customRender: "min" },
        },
        {
          title: this.$t("certification.max"),
          dataIndex: "max",
          key: "max",
          scopedSlots: { customRender: "max" },
        },
        {
          title: this.$t("appreciation.appreciation"),
          dataIndex: "appreciation",
          key: "appreciation",
          scopedSlots: { customRender: "appreciation" },
        },
        {
          title: this.$t("bulletin.subModule"),
          key: "submodule",
          scopedSlots: { customRender: "submodule" },
        },
        {
          title: this.$t("action.operation"),
          dataIndex: "operation",
          key: "operation",
          align: "center",
          fixed: "right",
          scopedSlots: { customRender: "operation" },
        },
      ];
    },
    targetModulesByLevel() {
      return this.targetModules.reduce((acc, m) => {
        const level = String(m.level);
        if (!acc[level]) acc[level] = [];
        acc[level].push(m);
        return acc;
      }, {});
    },
    existingAppreciationsColumns() {
      return [
        {
          title: this.$t("liste.nomPrenom"),
          key: "student",
          scopedSlots: { customRender: "student" },
        },
        {
          title: this.$t("etablissement.classe"),
          key: "classroom",
          scopedSlots: { customRender: "classroom" },
        },
        {
          title: this.$t("appreciation.appreciation"),
          dataIndex: "appreciation",
          key: "appreciation",
        },
        {
          title: this.$t("emploi.enseignant"),
          key: "teacher",
          scopedSlots: { customRender: "teacher" },
        },
      ];
    },
  },
  watch: {
    "filters.moduleType"() {
      this.filters.moduleId = null;
      this.$nextTick(() => {
        if (
          this.filters.moduleType &&
          this.filters.level &&
          this.filters.trimester &&
          !this.loadingModules
        ) {
          this.loadModules();
        }
      });
    },
    "filters.level"() {
      this.filters.moduleId = null;
      this.$nextTick(() => {
        if (
          this.filters.moduleType &&
          this.filters.level &&
          this.filters.trimester &&
          !this.loadingModules
        ) {
          this.loadModules();
        }
      });
    },
    "filters.trimester"() {
      this.filters.moduleId = null;
      this.$nextTick(() => {
        if (
          this.filters.moduleType &&
          this.filters.level &&
          this.filters.trimester &&
          !this.loadingModules
        ) {
          this.loadModules();
        }
      });
    },
    "filters.moduleId"() {
      this.filters.submoduleId = null;
      this.$nextTick(async () => {
        await this.updateAvailableSubmodules();
        if (this.filters.moduleId && this.settings.activeSchoolarYear) {
          this.loadAutoAppreciations();
        } else {
          this.appreciations = [];
        }
      });
    },
    "filters.submoduleId"() {
      if (this.filters.moduleId && this.settings.activeSchoolarYear) {
        this.loadAutoAppreciations();
      }
    },
    "cloneSource.moduleType"() {
      if (!this.visibleCloneModal) return;
      this.cloneSource.moduleId = null;
      this.cloneSource.submoduleId = null;
      this.$nextTick(() => {
        if (
          this.cloneSource.moduleType &&
          this.cloneSource.level &&
          this.cloneSource.trimester &&
          !this.loadingSourceModules
        ) {
          this.loadSourceModules();
        }
      });
    },
    "cloneSource.level"() {
      if (!this.visibleCloneModal) return;
      this.cloneSource.moduleId = null;
      this.cloneSource.submoduleId = null;
      this.$nextTick(() => {
        if (
          this.cloneSource.moduleType &&
          this.cloneSource.level &&
          this.cloneSource.trimester &&
          !this.loadingSourceModules
        ) {
          this.loadSourceModules();
        }
      });
    },
    "cloneSource.trimester"() {
      if (!this.visibleCloneModal) return;
      this.cloneSource.moduleId = null;
      this.cloneSource.submoduleId = null;
      this.$nextTick(() => {
        if (
          this.cloneSource.moduleType &&
          this.cloneSource.level &&
          this.cloneSource.trimester &&
          !this.loadingSourceModules
        ) {
          this.loadSourceModules();
        }
      });
    },
    "cloneTarget.moduleTypes"() {
      if (!this.visibleCloneModal) return;
      this.cloneTarget.moduleIds = [];
      this.debounceLoadTargetModules();
    },
    "cloneTarget.levels"() {
      if (!this.visibleCloneModal) return;
      this.cloneTarget.moduleIds = [];
      this.debounceLoadTargetModules();
    },
    "cloneTarget.trimesters"() {
      if (!this.visibleCloneModal) return;
      this.cloneTarget.moduleIds = [];
      this.debounceLoadTargetModules();
    },
  },
  async created() {
    this.loading = true;
    try {
      this.schoolType = this.activeBuilding.type;
      this.filters.moduleType = "MP";
      this.filters.level = this.defaultPrimaryLevel;
      this.filters.trimester = "1";

      const res = await apiClient.post("/buildings/filter", {
        query: { _id: this.settings.activeBuildingId },
      });
      const building = res.data[0] || {};
      this.predefinedAppreciations = building.appreciationOptions || [];

      // Load all submodules
      await this.loadAllSubmodules();

      await this.loadModules();
    } catch (e) {
      console.error(e);
      this.$message.error(this.$t("error.erreur"));
    } finally {
      this.loading = false;
    }
  },
  beforeDestroy() {
    if (this.targetModulesTimeout) {
      clearTimeout(this.targetModulesTimeout);
    }
  },
  methods: {
    handleApiError(error, defaultMessage) {
      const errorCode =
        error.response?.data?.errorCode || error.response?.data?.error?.errorCode;
      const rawMessage = error.response?.data?.message || "";

      // Check for PACK_PERMISSION_ERROR
      if (
        errorCode === "PACK_PERMISSION_ERROR"
      ) {
        this.$message.error(this.$t("errorCode.PACK_PERMISSION_ERROR"));
        return;
      }

      if (errorCode === "AUTO_APPRECIATION_MISSING_TEACHER_ACCESS") {
        let moduleName = "";
        let classrooms = "";
        try {
          const parsed = JSON.parse(rawMessage || "{}");
          moduleName = parsed.moduleName || "";
          classrooms = Array.isArray(parsed.classrooms)
            ? parsed.classrooms.join(", ")
            : "";
        } catch (e) {
          classrooms = rawMessage || "";
        }

        this.$message.error(
          this.$t("errorCode.AUTO_APPRECIATION_MISSING_TEACHER_ACCESS", {
            moduleName,
            classrooms,
          })
        );
        return;
      }

      // Fallback to default error message
      this.$message.error(
        rawMessage || defaultMessage || this.$t("error.erreur")
      );
    },
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      );
    },
    async loadModules() {
      if (
        !this.filters.level ||
        !this.filters.trimester ||
        !this.filters.moduleType ||
        this.loadingModules
      )
        return;
      this.loadingModules = true;
      try {
        const { data } = await apiClient.post("/bulletin/v2/module/filter", {
          query: {
            status: "active",
            type: this.filters.moduleType,
            level: this.filters.level,
            trimester: this.filters.trimester,
          },
        });
        this.modules = data || [];
        // Update available submodules when modules are loaded
        if (this.filters.moduleId) {
          await this.updateAvailableSubmodules();
        }
      } catch (e) {
        console.error(e);
        this.$message.error(this.$t("error.erreur"));
      } finally {
        this.loadingModules = false;
      }
    },
    async loadAllSubmodules() {
      try {
        const response = await apiClient.post("/bulletin/v2/sub-module/list", {
          status: "active",
        });
        this.allSubmodules = response.data || [];
      } catch (e) {
        console.error(e);
        this.allSubmodules = [];
      }
    },
    async updateAvailableSubmodules() {
      if (!this.filters.moduleId) {
        this.availableSubmodules = [];
        this.canAppModule = false;
        return;
      }
      const selectedModule = this.modules.find(
        (m) => m._id === this.filters.moduleId
      );
      if (!selectedModule || !selectedModule.subjectsInfo) {
        this.availableSubmodules = [];
        this.canAppModule = false;
        return;
      }

      // Extract unique submodule IDs from the module's subjectsInfo
      const submoduleIds = new Set();
      const subjectsInfo = selectedModule.subjectsInfo || {};
      Object.values(subjectsInfo).forEach((info) => {
        if (info.sousModule) {
          submoduleIds.add(String(info.sousModule));
        }
      });

      // Filter submodules that exist in this module
      this.availableSubmodules = this.allSubmodules.filter((sm) =>
        submoduleIds.has(String(sm._id))
      );

      // Check if any teacher has canAppModule: true for this module
      if (this.availableSubmodules.length > 0) {
        await this.checkCanAppModule();
        // Auto-select first submodule if canAppModule is false
        if (!this.canAppModule && this.availableSubmodules.length > 0) {
          this.filters.submoduleId = this.availableSubmodules[0]._id;
        }
      } else {
        this.canAppModule = false;
      }
    },
    async checkCanAppModule() {
      try {
        const { data } = await apiClient.post(
          "/bulletin/v2/auto-appreciation/check-can-app-module",
          { moduleId: this.filters.moduleId }
        );
        this.canAppModule = data?.canAppModule || false;
      } catch (e) {
        console.error(e);
        this.canAppModule = false;
      }
    },
    async handleModuleChange() {
      this.filters.submoduleId = null;
      await this.updateAvailableSubmodules();
    },
    handleSubmoduleChange() {
      if (this.filters.moduleId && this.settings.activeSchoolarYear) {
        this.loadAutoAppreciations();
      }
    },
    async loadAutoAppreciations() {
      if (!this.filters.moduleId || !this.settings.activeSchoolarYear) {
        this.appreciations = [];
        return;
      }
      this.loading = true;
      try {
        const payload = {
          moduleId: this.filters.moduleId,
          schoolYearId: this.settings.activeSchoolarYear,
        };
        if (this.filters.submoduleId) {
          payload.submoduleId = this.filters.submoduleId;
        }
        const response = await apiClient.post(
          "/bulletin/v2/auto-appreciation/",
          payload
        );
        this.appreciations = response.data.data || [];
      } catch (e) {
        console.error(e);
        this.$message.error(this.$t("error.erreur"));
        this.appreciations = [];
      } finally {
        this.loading = false;
      }
    },
    showAddModal() {
      if (!this.hasValidFilters) {
        this.$message.warning(this.$t("all.fillFilters"));
        return;
      }
      this.editingAppreciation = null;
      this.visibleModal = true;
      this.$nextTick(() => {
        this.form.resetFields();
      });
    },
    showEditModal(record) {
      this.editingAppreciation = record;
      this.visibleModal = true;
      this.$nextTick(() => {
        const isPredefined = this.predefinedAppreciations.find(
          (a) => a.appreciation === record.appreciation
        );
        this.form.setFieldsValue({
          min: record.min,
          max: record.max,
          appreciationPredefined: isPredefined ? record.appreciation : null,
          appreciationCustom: isPredefined ? "" : record.appreciation,
        });
      });
    },
    async handleSubmit(e) {
      e.preventDefault();
      this.form.validateFields(async (err, values) => {
        if (!err) {
          const appreciation =
            values.appreciationPredefined || values.appreciationCustom || "";
          if (!appreciation) {
            this.$message.warning(
              this.$t("appreciation.appreciation") +
                " " +
                this.$t("requis.required")
            );
            return;
          }

          this.saving = true;
          try {
            const payload = {
              moduleId: this.filters.moduleId,
              schoolYearId: this.settings.activeSchoolarYear,
              min: values.min,
              max: values.max,
              appreciation: appreciation,
            };
            if (this.filters.submoduleId) {
              payload.submoduleId = this.filters.submoduleId;
            }

            if (this.editingAppreciation && this.editingAppreciation._id) {
              // Update existing
              await apiClient.patch(
                `/bulletin/v2/auto-appreciation/${this.editingAppreciation._id}`,
                payload
              );
              this.$message.success(this.$t("success.appreciationOptionMSA"));
            } else {
              // Add new
              await apiClient.put("/bulletin/v2/auto-appreciation/", payload);
              this.$message.success(this.$t("success.appreciationOptionAjout"));
            }

            this.handleCancel();
            await this.loadAutoAppreciations();
          } catch (error) {
            console.error(error);
            this.handleApiError(error, this.$t("error.erreur"));
          } finally {
            this.saving = false;
          }
        }
      });
    },
    handleCancel() {
      this.visibleModal = false;
      this.editingAppreciation = null;
      this.form.resetFields();
    },
    handlePredefinedChange(value) {
      if (value) {
        this.form.setFieldsValue({ appreciationCustom: "" });
      }
    },
    handleCustomChange(e) {
      if (e.target.value) {
        this.form.setFieldsValue({ appreciationPredefined: null });
      }
    },
    async removeAppreciation(id) {
      try {
        await apiClient.delete(`/bulletin/v2/auto-appreciation/${id}`);
        this.$message.success(this.$t("success.appreciationOptionSupp"));
        await this.loadAutoAppreciations();
      } catch (error) {
        console.error(error);
        this.handleApiError(error, this.$t("error.erreur"));
      }
    },
    async applyAutomaticAppreciation() {
      if (!this.hasValidFilters || this.appreciations.length === 0) {
        return;
      }

      this.loadingExistingAppreciations = true;
      this.visibleExistingAppreciationsModal = true;
      this.selectedAppreciationIds = [];

      try {
        const payload = {
          moduleId: this.filters.moduleId,
          schoolYearId: this.settings.activeSchoolarYear,
          trimester: this.filters.trimester,
          moduleType: this.filters.moduleType,
        };
        if (this.filters.submoduleId) {
          payload.submoduleId = this.filters.submoduleId;
        }
        const response = await apiClient.post(
          "/bulletin/v2/auto-appreciation/check-existent-appreciation",
          payload
        );
        this.existingAppreciations = response.data.data || [];
      } catch (error) {
        console.error(error);
        this.handleApiError(error, this.$t("error.erreur"));
        this.visibleExistingAppreciationsModal = false;
      } finally {
        this.loadingExistingAppreciations = false;
      }
    },
    onSelectAppreciations(selectedRowKeys) {
      this.selectedAppreciationIds = selectedRowKeys;
    },
    handleCancelExistingModal() {
      this.visibleExistingAppreciationsModal = false;
      this.existingAppreciations = [];
      this.selectedAppreciationIds = [];
    },
    async confirmApplyAppreciations() {
      if (!this.hasValidFilters || this.appreciations.length === 0) {
        this.$message.warning(this.$t("all.fillFilters"));
        return;
      }

      this.applyingAppreciations = true;
      try {
        const ignoredAppreciationsIds = this.existingAppreciations
          .map((a) => a._id)
          .filter((id) => !this.selectedAppreciationIds.includes(id));

        const payload = {
          moduleId: this.filters.moduleId,
          schoolYearId: this.settings.activeSchoolarYear,
          trimester: this.filters.trimester,
          moduleType: this.filters.moduleType,
          ignoredAppreciationsIds,
        };
        if (this.filters.submoduleId) {
          payload.submoduleId = this.filters.submoduleId;
        }

        const response = await apiClient.post(
          "/bulletin/v2/auto-appreciation/apply",
          payload
        );

        const applied =
          (response.data &&
            response.data.data &&
            Number(response.data.data.applied)) ||
          0;

        this.$message.success(
          this.$t("appreciation.applySuccess", { count: applied })
        );

        this.handleCancelExistingModal();
      } catch (error) {
        console.error(error);
        this.handleApiError(error, this.$t("error.erreur"));
      } finally {
        this.applyingAppreciations = false;
      }
    },
    handleClone() {
      this.visibleCloneModal = true;
      this.cloneSource = {
        moduleType: this.filters.moduleType || "MP",
        level: this.filters.level || this.defaultPrimaryLevel,
        trimester: this.filters.trimester || "1",
        moduleId: this.filters.moduleId,
        submoduleId: this.filters.submoduleId || null,
        schoolYear: this.settings.activeSchoolarYear || null,
      };
      this.cloneTarget = {
        moduleTypes: [],
        levels: [],
        trimesters: [],
        schoolYear: this.settings.activeSchoolarYear || null,
        moduleIds: [],
      };
      this.fetchSchoolYears();
      this.$nextTick(() => {
        if (
          this.cloneSource.moduleType &&
          this.cloneSource.level &&
          this.cloneSource.trimester
        ) {
          this.loadSourceModules();
        }
      });
    },
    async fetchSchoolYears() {
      try {
        const response = await apiClient.post("/schoolaryears/filter", {
          query: {},
        });
        this.schoolYearsList = response.data.map((elem) => ({
          ...elem,
          key: elem._id,
        }));
        if (this.schoolYearsList.length > 0) {
          if (!this.cloneSource.schoolYear) {
            this.cloneSource.schoolYear =
              this.settings.activeSchoolarYear || this.schoolYearsList[0]._id;
          }
          if (!this.cloneTarget.schoolYear) {
            this.cloneTarget.schoolYear =
              this.settings.activeSchoolarYear || this.schoolYearsList[0]._id;
          }
        }
      } catch (error) {
        console.error("Error fetching school years:", error);
        this.$message.error(this.$t("error.erreur"));
      }
    },
    formatSchoolYear(sy) {
      if (!sy || !sy.start || !sy.end) return "";
      const start = moment(sy.start).format("YYYY");
      const end = moment(sy.end).format("YYYY");
      return `${start}/${end}`;
    },
    async loadSourceModules() {
      if (
        !this.cloneSource.moduleType ||
        !this.cloneSource.level ||
        !this.cloneSource.trimester ||
        this.loadingSourceModules
      )
        return;
      this.loadingSourceModules = true;
      try {
        const { data } = await apiClient.post("/bulletin/v2/module/filter", {
          query: {
            status: "active",
            type: this.cloneSource.moduleType,
            level: this.cloneSource.level,
            trimester: this.cloneSource.trimester,
          },
        });
        this.sourceModules = data || [];
        await this.updateCloneSourceAvailableSubmodules();
      } catch (e) {
        console.error(e);
        this.$message.error(this.$t("error.erreur"));
      } finally {
        this.loadingSourceModules = false;
      }
    },
    async updateCloneSourceAvailableSubmodules() {
      if (!this.cloneSource.moduleId) {
        this.cloneSourceAvailableSubmodules = [];
        this.cloneSourceCanAppModule = false;
        return;
      }
      const selectedModule = this.sourceModules.find(
        (m) => m._id === this.cloneSource.moduleId
      );
      if (!selectedModule || !selectedModule.subjectsInfo) {
        this.cloneSourceAvailableSubmodules = [];
        this.cloneSourceCanAppModule = false;
        return;
      }

      const submoduleIds = new Set();
      const subjectsInfo = selectedModule.subjectsInfo || {};
      Object.values(subjectsInfo).forEach((info) => {
        if (info.sousModule) {
          submoduleIds.add(String(info.sousModule));
        }
      });

      this.cloneSourceAvailableSubmodules = this.allSubmodules.filter((sm) =>
        submoduleIds.has(String(sm._id))
      );

      if (this.cloneSourceAvailableSubmodules.length > 0) {
        await this.checkCloneSourceCanAppModule();
        if (!this.cloneSourceCanAppModule) {
          this.cloneSource.submoduleId = this.cloneSourceAvailableSubmodules[0]._id;
        }
      } else {
        this.cloneSourceCanAppModule = false;
      }
    },
    async checkCloneSourceCanAppModule() {
      try {
        const { data } = await apiClient.post(
          "/bulletin/v2/auto-appreciation/check-can-app-module",
          { moduleId: this.cloneSource.moduleId }
        );
        this.cloneSourceCanAppModule = data?.canAppModule || false;
      } catch (e) {
        console.error(e);
        this.cloneSourceCanAppModule = false;
      }
    },
    async handleCloneSourceModuleChange(value) {
      this.cloneSource.moduleId = value;
      this.cloneSource.submoduleId = null;
      await this.updateCloneSourceAvailableSubmodules();
    },
    debounceLoadTargetModules() {
      if (this.selectingAllClone) {
        return;
      }
      if (this.targetModulesTimeout) {
        clearTimeout(this.targetModulesTimeout);
      }
      this.targetModulesTimeout = setTimeout(() => {
        this.loadTargetModules();
      }, 300);
    },
    async loadTargetModules() {
      if (
        !this.cloneTarget.moduleTypes.length ||
        !this.cloneTarget.levels.length ||
        !this.cloneTarget.trimesters.length
      ) {
        this.targetModules = [];
        return;
      }
      this.loadingTargetModules = true;
      try {
        const promises = [];

        for (const type of this.cloneTarget.moduleTypes) {
          for (const level of this.cloneTarget.levels) {
            for (const trimester of this.cloneTarget.trimesters) {
              promises.push(
                apiClient.post("/bulletin/v2/module/filter", {
                  query: {
                    status: "active",
                    type: type,
                    level: level,
                    trimester: trimester,
                  },
                })
              );
            }
          }
        }

        const results = await Promise.all(promises);
        const moduleMap = new Map();

        results.forEach((response) => {
          (response.data || []).forEach((module) => {
            if (!moduleMap.has(module._id)) {
              moduleMap.set(module._id, module);
            }
          });
        });

        this.targetModules = Array.from(moduleMap.values());
      } catch (e) {
        console.error(e);
        this.$message.error(this.$t("error.erreur"));
      } finally {
        this.loadingTargetModules = false;
      }
    },
    async selectAllClone() {
      this.selectingAllClone = true;
      this.cloneTarget.moduleTypes = ["MP", "MT", "MS"];
      this.cloneTarget.levels = Object.keys(this.levelSelectData || {});
      this.cloneTarget.trimesters = ["1", "2", "3"];
      await this.loadSelectedAllTargetModules();
    },
    async loadSelectedAllTargetModules() {
      this.loadingTargetModules = true;
      try {
        const { data } = await apiClient.post("/bulletin/v2/module/filter", {
          query: {
            status: "active",
            type: { $in: this.cloneTarget.moduleTypes },
            level: { $in: this.cloneTarget.levels },
            trimester: { $in: this.cloneTarget.trimesters },
          },
        });
        this.targetModules = data || [];
        this.cloneTarget.moduleIds = this.targetModules.map((m) => m._id);
      } catch (e) {
        console.error(e);
        this.$message.error(this.$t("error.erreur"));
      } finally {
        this.loadingTargetModules = false;
        this.selectingAllClone = false;
      }
    },
    handleCloneCancel() {
      this.visibleCloneModal = false;
      if (this.targetModulesTimeout) {
        clearTimeout(this.targetModulesTimeout);
        this.targetModulesTimeout = null;
      }
      this.resetCloneForm();
    },
    resetCloneForm() {
      this.cloneSource = {
        moduleType: null,
        level: null,
        trimester: null,
        moduleId: null,
        submoduleId: null,
        schoolYear: null,
      };
      this.cloneTarget = {
        moduleTypes: [],
        levels: [],
        trimesters: [],
        schoolYear: null,
        moduleIds: [],
      };
      this.sourceModules = [];
      this.targetModules = [];
    },
    async executeClone() {
      // Validate source
      if (!this.cloneSource.moduleId || !this.cloneSource.schoolYear) {
        this.$message.warning(this.$t("appreciation.selectSourceModule"));
        return;
      }

      // Validate target
      if (!this.cloneTarget.schoolYear) {
        this.$message.warning(this.$t("appreciation.selectTargetSchoolYear"));
        return;
      }

      // Get target module IDs
      let targetModuleIds = [];

      // If specific modules are selected, use them
      if (this.cloneTarget.moduleIds && this.cloneTarget.moduleIds.length > 0) {
        targetModuleIds = this.cloneTarget.moduleIds;
      } else if (
        this.cloneTarget.moduleTypes.length > 0 &&
        this.cloneTarget.levels.length > 0 &&
        this.cloneTarget.trimesters.length > 0
      ) {
        // Get modules based on filters
        try {
          const promises = [];
          for (const type of this.cloneTarget.moduleTypes) {
            for (const level of this.cloneTarget.levels) {
              for (const trimester of this.cloneTarget.trimesters) {
                promises.push(
                  apiClient.post("/bulletin/v2/module/filter", {
                    query: {
                      status: "active",
                      type: type,
                      level: level,
                      trimester: trimester,
                    },
                  })
                );
              }
            }
          }
          const results = await Promise.all(promises);
          const moduleMap = new Map();
          results.forEach((response) => {
            (response.data || []).forEach((module) => {
              if (!moduleMap.has(module._id)) {
                moduleMap.set(module._id, module);
              }
            });
          });
          targetModuleIds = Array.from(moduleMap.keys());
        } catch (error) {
          console.error(error);
          this.$message.error(this.$t("error.erreur"));
          return;
        }
      } else {
        this.$message.warning(this.$t("appreciation.selectTargetModules"));
        return;
      }

      if (targetModuleIds.length === 0) {
        this.$message.warning(this.$t("appreciation.noTargetModulesFound"));
        return;
      }

      // Call clone API
      try {
        const clonePayload = {
          sourceModuleId: this.cloneSource.moduleId,
          sourceSchoolYearId: this.cloneSource.schoolYear,
          targetModuleIds: targetModuleIds,
          targetSchoolYearId: this.cloneTarget.schoolYear,
        };
        if (this.cloneSource.submoduleId) {
          clonePayload.sourceSubmoduleId = this.cloneSource.submoduleId;
        }
        const response = await apiClient.post(
          "/bulletin/v2/auto-appreciation/clone",
          clonePayload
        );
        this.$message.success(this.$t("action.cloneSuccess"));
        this.handleCloneCancel();
      } catch (error) {
        console.error(error);
        this.handleApiError(error, this.$t("appreciation.noAutoAppreciationToClone"));
      }
    },
  },
};
</script>

<style scoped>
.mb-2 {
  margin-bottom: 8px;
}
</style>
